import { ArrowUp, Lock, Save, X } from 'lucide-react'
import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { track } from '../lib/analytics'
import {
  BracketHeader,
  BracketRoundRenderer,
  BracketViewToggleBar,
  JoinBracketDialog,
  MissingPicksDialog,
  PoolPlayRenderer,
} from '../components/bracket'
import Layout from '../components/Layout'
import LeaderboardTable from '../components/LeaderboardTable'
import { Button } from '../components/ui/button'
import { useNotification } from '../contexts/NotificationContext'
import { useUser } from '../contexts/UserContext'
import { fetchEntries } from '../services/bracketService'
import useBracketStore from '../stores/useBracketStore'

export default function BracketPicks() {
  const { bracketId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useUser()
  const notify = useNotification()

  const entryIdFromUrl = searchParams.get('entryId')

  // ── Store selectors ──
  const loading = useBracketStore(s => s.loading)
  const error = useBracketStore(s => s.error)
  const tournament = useBracketStore(s => s.tournament)
  const bracket = useBracketStore(s => s.bracket)
  const canEditPicks = useBracketStore(s => s.canEditPicks)
  const viewingLeaderboard = useBracketStore(s => s.viewingLeaderboard)
  const leaderboard = useBracketStore(s => s.leaderboard)
  const leaderboardLoading = useBracketStore(s => s.leaderboardLoading)
  const isLockedBannerDismissed = useBracketStore(s => s.isLockedBannerDismissed)
  const setIsLockedBannerDismissed = useBracketStore(s => s.setIsLockedBannerDismissed)
  const showBackToTop = useBracketStore(s => s.showBackToTop)
  const hasPicksChanged = useBracketStore(s => s.hasPicksChanged)
  const inlineSaveInView = useBracketStore(s => s.inlineSaveInView)
  const isViewingOfficial = useBracketStore(s => s.isViewingOfficial)
  const isOwnEntry = useBracketStore(s => s.isOwnEntry)
  const isAdmin = useBracketStore(s => s.isAdmin)
  const userSaveLabel = useBracketStore(s => s.userSaveLabel)
  const setIsMobile = useBracketStore(s => s.setIsMobile)
  const setViewMode = useBracketStore(s => s.setViewMode)
  const userEntries = useBracketStore(s => s.userEntries)
  const userEntriesLoaded = useBracketStore(s => s.userEntriesLoaded)
  const matches = useBracketStore(s => s.matches)
  const picks = useBracketStore(s => s.picks)
  const officialEntryId = useBracketStore(s => s.officialEntryId)
  const selectedEntryId = useBracketStore(s => s.selectedEntryId)
  const leaderboardGroupFilter = useBracketStore(s => s.leaderboardGroupFilter)
  const usesRoundLocks = useBracketStore(s => s.usesRoundLocks)
  const roundStates = useBracketStore(s => s.roundStates)

  // A multi-week bracket stays 'open' for its whole run, so "is anything
  // pickable right now" is a question about its rounds, not its status. Between
  // weekends every round is either locked or waiting on the league to announce
  // the next set of matchups — say which, rather than the flat "locked".
  const lockedBannerMessage = (() => {
    if (canEditPicks) return null
    if (usesRoundLocks) {
      if (!roundStates) return null
      const rounds = Object.values(roundStates)
      if (rounds.some(r => r.status === 'open')) return null
      const nextUp = rounds
        .filter(r => r.status === 'pending')
        .sort((a, b) => a.round - b.round)[0]
      if (!nextUp) return 'All rounds are locked'
      return nextUp.matchupsPublished
        ? `${nextUp.label || `Round ${nextUp.round}`} picks open soon`
        : `Waiting on ${nextUp.label || `Round ${nextUp.round}`} matchups`
    }
    return bracket?.status !== 'open' ? 'Bracket is locked' : null
  })()

  // ── Sync external dependencies into the store ──
  useEffect(() => {
    useBracketStore.getState().syncDeps(user, navigate, notify, searchParams, setSearchParams)
  }, [user, navigate, notify, searchParams, setSearchParams])

  // ── Sync entryId from URL ──
  useEffect(() => {
    useBracketStore.getState().syncEntryFromUrl(entryIdFromUrl)
  }, [entryIdFromUrl])

  // ── Initialize on mount / bracketId change ──
  useEffect(() => {
    if (!bracketId) return
    track('bracket_viewed', { bracketId })
    const store = useBracketStore.getState()
    store.reset()
    store.initialize(bracketId, user, navigate, notify, searchParams, setSearchParams)
    store.fetchBracketData()
    return () => useBracketStore.getState().reset()
    // Only re-run when bracketId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketId])

  // ── Load user entries when bracket data is ready ──
  useEffect(() => {
    if (bracketId && user && !loading) {
      const store = useBracketStore.getState()
      store.loadUserEntries(user)
      store.loadOwnedGroups()
    }
  }, [bracketId, user, loading])

  // ── Locked or complete bracket with no entries: show official ──
  useEffect(() => {
    if (!userEntriesLoaded || entryIdFromUrl || userEntries.length > 0) return
    // A round-locked bracket stays 'open' all run, so "nothing to pick right
    // now" means every round is locked or still awaiting matchups — between
    // weekends, land an entry-less visitor on the official bracket rather than
    // an empty picker.
    const nothingPickable = usesRoundLocks
      ? !Object.values(roundStates || {}).some(r => r.status === 'open')
      : (bracket?.status === 'locked' || bracket?.status === 'complete')
    if (!nothingPickable) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('entryId', 'official')
    newParams.delete('autoCreate')
    useBracketStore.getState().setViewingLeaderboard(false)
    setSearchParams(newParams, { replace: true })
  }, [userEntriesLoaded, entryIdFromUrl, bracket?.status, usesRoundLocks, roundStates, userEntries.length, searchParams, setSearchParams])

  // ── Auto-create entry ──
  useEffect(() => {
    const autoCreate = searchParams.get('autoCreate')
    if (autoCreate === 'true' && bracketId && user && bracket && userEntries.length === 0) {
      if (bracket.status === 'open') {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('autoCreate')
        setSearchParams(newParams, { replace: true })
        useBracketStore.getState().handleCreateEntry()
      }
    }
  }, [searchParams, bracketId, user, bracket, userEntries.length, setSearchParams])

  // ── Load leaderboard ──
  useEffect(() => {
    if (viewingLeaderboard && bracket) {
      useBracketStore.getState().loadLeaderboard(leaderboardGroupFilter)
    }
  }, [viewingLeaderboard, leaderboardGroupFilter, bracket])

  // ── Load picks when matches are loaded ──
  useEffect(() => {
    if (matches.length > 0 && bracketId) {
      useBracketStore.getState().loadEntryPicks()
    }
  }, [matches.length, bracketId, officialEntryId, selectedEntryId])

  // ── When arriving from a notification, jump to the most recently updated round once picks load. ──
  // Depends on `picks` too — matches arrive before picks, and without picks there are no
  // `winner_team_id` values for `computeLatestUpdatedRoundIndex` to find.
  useEffect(() => {
    const fromNotification = searchParams.get('from') === 'notification'
    if (!fromNotification) return
    if (!matches.length) return
    if (!picks || Object.keys(picks).length === 0) return
    const store = useBracketStore.getState()
    const jumped = store.jumpToLatestUpdatedRound()
    if (jumped) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('from')
      setSearchParams(newParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.length, picks, officialEntryId, selectedEntryId])

  // ── Detect mobile viewport ──
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 640px)')

    const apply = (matched) => {
      setIsMobile(matched)
      if (matched) setViewMode('compact')
    }

    apply(mq.matches)

    const handler = (e) => apply(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', handler)
    else mq.addListener(handler)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler)
      else mq.removeListener(handler)
    }
  }, [setIsMobile, setViewMode])

  // ── Keyboard shortcut: Ctrl/Cmd+S ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (useBracketStore.getState().canEditPicks) {
          useBracketStore.getState().handleSave()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ── Reset mobile round index on bracket change ──
  // Skip the reset when the user arrived via a notification — the jump-to-latest-round
  // effect will set the indexes itself, and we don't want to clobber that with 0.
  useEffect(() => {
    if (!bracketId) return
    if (searchParams.get('from') === 'notification') return
    useBracketStore.getState().setMobileRoundIndex(0)
    useBracketStore.getState().setDisplayRoundIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketId])

  // ── Early returns ──

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading bracket...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md">
            <div className="mb-4 text-destructive">
              <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2">Unable to Load Bracket</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/tournaments')} variant="outline">
                Back to Tournaments
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  if (!tournament || !bracket) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md">
            <p className="text-muted-foreground mb-4">Bracket data is incomplete</p>
            <Button onClick={() => navigate('/tournaments')}>
              Back to Tournaments
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  // ── Main render ──

  // Same control as the toggle bar's inline Save button (same visibility
  // rules + label); it fades in bottom-right only while that button is
  // scrolled out of view, so exactly one Save button is on screen at a time.
  const saveFabVisible = canEditPicks && hasPicksChanged && !viewingLeaderboard &&
    (isViewingOfficial ? isAdmin : isOwnEntry) && !inlineSaveInView

  return (
    <Layout>
      <BracketHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Locked banner */}
        {lockedBannerMessage && !isLockedBannerDismissed && !(tournament?.end_date && new Date(tournament.end_date) < new Date()) && (
          <div className="mb-4 p-4 rounded-lg flex items-center gap-3 max-w-2xl banner-glass banner-glass-amber">
            <Lock className="h-5 w-5 text-[var(--orange-11)] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[var(--orange-12)] leading-none">
                {lockedBannerMessage}
              </p>
            </div>
            <button
              onClick={() => setIsLockedBannerDismissed(true)}
              className="flex-shrink-0 p-1 hover:bg-[var(--orange-3)] rounded transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4 text-[var(--orange-11)]" />
            </button>
          </div>
        )}

        {/* View Toggle Bar */}
        <BracketViewToggleBar />

        {/* Leaderboard or Bracket */}
        {viewingLeaderboard ? (
          <LeaderboardTable
            leaderboard={leaderboard}
            loading={leaderboardLoading}
            scope="bracket"
            emptyMessage="No entries found for this bracket"
            onUserClick={async (entry) => {
              const store = useBracketStore.getState()
              if (entry.is_current_user) {
                // DUB-192/DUB-183: the URL may still point at another user's
                // entry (or at the official entry in editOfficial mode) from a
                // previous click — re-point it at the viewer's own entry
                // instead of just flipping the view back.
                const own = userEntries.find(e => e.group_id === null) || userEntries[0]
                const newParams = new URLSearchParams(searchParams)
                newParams.delete('mode')
                if (own?.id) newParams.set('entryId', own.id)
                else newParams.delete('entryId')
                setSearchParams(newParams)
                store.setViewingLeaderboard(false)
                return
              }
              // Owner 2026-05-21: can't view another player's picks until lock.
              // DUB-150/151/152: admins are NOT exempt — the rule applies to
              // everyone in the normal browse flow (admins were able to reach
              // picks here while the profile path correctly blocked them).
              // On a multi-week bracket the bracket itself never reaches
              // 'locked' mid-run, so the gate is "has any round locked yet" —
              // once a round is in the books its picks are public.
              const picksArePublic = usesRoundLocks
                ? Object.values(roundStates || {}).some(r => r.status === 'locked' || r.status === 'complete')
                : ['locked', 'complete'].includes(bracket?.status)
              if (!picksArePublic) {
                notify.warning("You can view other players' picks once the bracket locks.")
                return
              }
              try {
                const entries = await fetchEntries(entry.user_id, bracketId)
                const theirEntry = Array.isArray(entries) ? entries[0] : null
                if (!theirEntry?.id) {
                  notify.error(`${entry.full_name || 'That user'} has no entry on this bracket yet`)
                  return
                }
                const newParams = new URLSearchParams(searchParams)
                newParams.set('entryId', theirEntry.id)
                setSearchParams(newParams)
                store.setViewingLeaderboard(false)
              } catch (err) {
                console.error("Failed to load user's bracket entry:", err)
                notify.error("Couldn't load that user's picks")
              }
            }}
          />
        ) : bracket?.format === 'pool_then_sunday' ? (
          <PoolPlayRenderer />
        ) : bracket?.format ? (
          <BracketRoundRenderer />
        ) : (
          // Guard: don't default to the single-elim view before the bracket's
          // format has hydrated — avoids a wrong-view flash on first load.
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}
      </div>

      {/* DUB-139/140: floating Save button, kept reachable without scrolling
          to the top so unsaved picks (e.g. Finals winners) aren't lost on
          navigation. Icon-only circle pinned to the very bottom-right corner;
          the Back-to-Top arrow shifts up while this is visible. */}
      {canEditPicks && hasPicksChanged && !viewingLeaderboard && (isViewingOfficial ? isAdmin : isOwnEntry) && (
        <Button
          variant="primaryNeon"
          type="button"
          onClick={() => useBracketStore.getState().handleSave()}
          className={`fixed bottom-4 right-4 z-40 h-12 w-12 !p-0 rounded-full shadow-green-md-inset transition-[opacity,transform] duration-300 ease-out ${saveFabVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}
          aria-label={isViewingOfficial ? 'Save Official' : userSaveLabel}
        >
          <Save className="h-5 w-5" />
        </Button>
      )}

      {/* Back to Top Button */}
      <button
        type="button"
        onClick={() => {
          // Two scroll surfaces can be non-zero on this page: the window
          // (page-level), and the bracket's internal scroll container
          // (overflowY:auto + maxHeight on desktop). showBackToTop appears
          // when EITHER is scrolled, so the click must reset both — earlier
          // it only reset the window, leaving the FAB inert when the user
          // had only scrolled inside the bracket. The scroll port is tagged
          // with `data-bracket-scrollport` in BracketRoundRenderer.
          window.scrollTo({ top: 0, behavior: 'smooth' })
          document
            .querySelector('[data-bracket-scrollport]')
            ?.scrollTo({ top: 0, behavior: 'smooth' })
        }}
        className={`btn-primary-neon fixed ${saveFabVisible ? 'bottom-[4.5rem] right-4' : 'bottom-20 right-5 sm:bottom-6 sm:right-6'} z-40 h-11 w-11 !p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shadow-green-md-inset transition-[opacity,transform,bottom,right] duration-300 ease-out ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5 mx-auto" />
      </button>

      {/* Dialogs */}
      <JoinBracketDialog />
      <MissingPicksDialog />
    </Layout>
  )
}
